import { memo, useCallback, useMemo, useState } from 'react'
import { Page } from 'react-pdf'
import type { CustomTextRenderer } from 'react-pdf/dist/shared/types.js'

import { Highlight } from '@/api/services/highlight'
import { Spinner } from '@/components/ui/spinner'

import { HIGHLIGHT_COLORS } from '../../highlight/highlight-colors'
import type { HighlightArea } from '../highlight-areas'

const IS_MOBILE =
  typeof navigator !== 'undefined' &&
  (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 1 && /Macintosh/i.test(navigator.userAgent)))
// モバイルは canvas メモリが厳しいため 1 ページの canvas 画素数を抑える（iPhone の幅合わせ 3x で約 1.8M px）
const MOBILE_MAX_CANVAS_PIXELS = 4_000_000
const DEFAULT_PAGE_RATIO = 0.707

function getPageDevicePixelRatio(cssWidth: number | undefined, cssHeight: number | undefined): number | undefined {
  if (!IS_MOBILE) return undefined
  const deviceRatio = window.devicePixelRatio || 1
  if (!cssWidth && !cssHeight) return deviceRatio
  // 片方しか無いときは A 判の縦横比で見積もる
  const w = cssWidth ?? (cssHeight ?? 0) * DEFAULT_PAGE_RATIO
  const h = cssHeight ?? (cssWidth ?? 0) / DEFAULT_PAGE_RATIO
  const budgetRatio = Math.sqrt(MOBILE_MAX_CANVAS_PIXELS / (w * h))
  return Math.max(1, Math.min(deviceRatio, budgetRatio))
}

// 読み込み中の表示は PdfPage 側で出すため、react-pdf 内の loading 表示は不要
// （何も渡さないと "Loading page..." というデフォルトテキストが表示される）
const EMPTY_LOADING = <></>

export type { HighlightArea }

type PdfPageProps = {
  pageIndex: number
  width: number | undefined
  /** 高さ基準で描画する場合の高さ (px)。width が指定されている場合は無視される */
  height?: number
  isVisible: boolean
  highlights: Highlight[]
  /** プレースホルダー用の実寸サイズ (px) */
  estimatedWidth?: number
  estimatedHeight?: number
  searchHighlightRenderer?: CustomTextRenderer
  onHighlightClick: (highlight: Highlight, e: React.MouseEvent) => void
  /** canvas 描画が成功・失敗のどちらかで終わった時 */
  onRenderSettled?: () => void
}

/** 単一PDFページ: react-pdf <Page> + ハイライトオーバーレイ */
export const PdfPage = memo(function PdfPage({
  pageIndex,
  width,
  height,
  isVisible,
  highlights,
  estimatedWidth,
  estimatedHeight,
  searchHighlightRenderer,
  onHighlightClick,
  onRenderSettled,
}: PdfPageProps) {
  // react-pdf の Page は描画前から白地で出るため、描画が終わるまで読み込み中の表示を上に重ねる
  const [isRendered, setIsRendered] = useState(false)
  // 描画範囲から外れると canvas は破棄されるので、次に表示するときは読み込み中から出す
  if (!isVisible && isRendered) setIsRendered(false)

  const handleRenderSettled = useCallback(() => {
    setIsRendered(true)
    onRenderSettled?.()
  }, [onRenderSettled])

  // このページのハイライトを抽出
  const pageHighlights = useMemo(
    () =>
      highlights.filter((h) => {
        const areas = h.position_data.highlightAreas as HighlightArea[] | undefined
        return areas?.some((a) => a.pageIndex === pageIndex)
      }),
    [highlights, pageIndex],
  )

  const renderHighlightOverlay = useCallback(
    () => (
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 3 }}>
        {pageHighlights.map((highlight) => {
          const areas = highlight.position_data.highlightAreas as HighlightArea[]
          const filteredAreas = areas.filter((area) => area.pageIndex === pageIndex)
          const colorConfig = HIGHLIGHT_COLORS.find((c) => c.value === highlight.color)
          const fillColor = colorConfig?.hex || 'yellow'
          // ハイライトごとに親にopacityを適用し、矩形が重なっても透明度が二重にならないようにする
          return (
            <div key={highlight.id} className="absolute inset-0 pointer-events-none" style={{ opacity: 0.4 }}>
              {filteredAreas.map((area, idx) => (
                <div
                  key={idx}
                  data-highlight-area="true"
                  onClick={(e) => {
                    e.stopPropagation()
                    onHighlightClick(highlight, e)
                  }}
                  style={{
                    position: 'absolute',
                    background: fillColor,
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    left: `${area.left}%`,
                    top: `${area.top}%`,
                    width: `${area.width}%`,
                    height: `${area.height}%`,
                  }}
                />
              ))}
            </div>
          )
        })}
      </div>
    ),
    [pageHighlights, pageIndex, onHighlightClick],
  )

  // ラッパー div のスタイル
  // 推定サイズが確定している場合はラッパーの外形を固定し、中身の描画状態に関わらず
  // レイアウトが揺れないようにする（iOS Safari は overflow-anchor 未対応で
  // 1px のずれでも下のページが押し出されて "一瞬別ページが見える" 現象を起こす）
  const wrapperStyle = useMemo<React.CSSProperties>(() => {
    if (estimatedWidth && estimatedHeight) {
      return {
        width: `${estimatedWidth}px`,
        height: `${estimatedHeight}px`,
      }
    }
    if (height) {
      return {
        height: `${height}px`,
        aspectRatio: 'var(--pdf-page-ratio, 0.707)',
      }
    }
    return {
      width: width ? `${width}px` : '100%',
      aspectRatio: 'var(--pdf-page-ratio, 0.707)',
    }
  }, [estimatedWidth, estimatedHeight, width, height])

  const devicePixelRatio = useMemo(
    () => getPageDevicePixelRatio(estimatedWidth ?? width, estimatedHeight ?? height),
    [estimatedWidth, estimatedHeight, width, height],
  )

  return (
    <div className="pdf-page relative overflow-hidden" data-page-index={pageIndex} style={wrapperStyle}>
      {isVisible && (
        <>
          <Page
            pageIndex={pageIndex}
            width={width}
            height={height}
            devicePixelRatio={devicePixelRatio}
            renderTextLayer={true}
            renderAnnotationLayer={true}
            customTextRenderer={searchHighlightRenderer}
            loading={EMPTY_LOADING}
            onRenderSuccess={handleRenderSettled}
            onRenderError={handleRenderSettled}
          />
          {pageHighlights.length > 0 && renderHighlightOverlay()}
        </>
      )}
      {!isRendered && (
        <div className="absolute inset-0 z-5 flex items-center justify-center bg-neutral-900 text-neutral-400">
          {isVisible && <Spinner size={32} />}
        </div>
      )}
    </div>
  )
})
